import type { DealFromQuery } from '@/states/deal.state';
import { resolveAwaitedProcessedDatasource } from '@/lib/datasource-import-resolution';
import {
  clearDealItemsInProgressToast,
  pendingDatasourceToastIds,
} from '@/lib/deal-items-toast';
import { DataSourceStatus } from '@/lib/graphql/generated/graphql';
import {
  showDataSourceErrorToast,
  type ShowDataSourceErrorToastParams,
} from '@/lib/utils/datasource-error';
import { reconcilePendingDatasources } from '@/lib/utils/datasource-reconciliation';
import { dealItemsRefetchState } from '@/states/products.state';
import { datasourceProcessedSignalAtom } from '@/states/shopify-category-fallback.state';
import type { ApolloClient } from '@apollo/client';
import type { TFunction } from 'i18next';
import { getDefaultStore } from 'jotai';

type DealQueryDataSource = DealFromQuery['dataSources'][number];

type DealSignalTracking = {
  dealId: string;
  activeVersionId: string | null;
  statusByDatasourceId: Map<string, DataSourceStatus | null>;
};

/**
 * Dernier état observé, mis à jour de façon **synchrone** à chaque résolution.
 *
 * Le suivi est tenu ici plutôt que lu depuis `dealAtom` parce que cet atome est
 * alimenté par un effet : deux signaux `DEAL` rapprochés y liraient le même état
 * d'avant et rejoueraient donc la même transition, produisant un toast d'erreur en
 * double. C'est l'équivalent du `previousDataSourcesStatusRef` du chemin
 * WebSocket, sans la latence.
 *
 * Un seul deal est suivi à la fois, et non une table par identifiant : arriver sur
 * un deal avec l'instantané d'une visite précédente ferait diffuser des transitions
 * survenues pendant l'absence, donc un toast d'erreur pour un échec ancien. Comme
 * `DealLayout` n'est pas remonté au changement de deal (`src/router.tsx`, pas de
 * `key`), rien d'autre ne purgerait ce suivi. Repartir de zéro dès que le deal
 * change reproduit la règle d'initialisation ci-dessous.
 */
let tracking: DealSignalTracking | null = null;

function trackingFor(deal: DealFromQuery): DealSignalTracking | undefined {
  return tracking?.dealId === deal.id ? tracking : undefined;
}

function datasourceStatusOf(
  dataSource: DealQueryDataSource
): DataSourceStatus | null {
  return 'status' in dataSource ? (dataSource.status ?? null) : null;
}

function rememberDealState(deal: DealFromQuery): void {
  tracking = {
    dealId: deal.id,
    activeVersionId: deal.activeVersionId ?? null,
    statusByDatasourceId: new Map(
      deal.dataSources.map((dataSource) => [
        dataSource.id,
        datasourceStatusOf(dataSource),
      ])
    ),
  };
}

function refetchDealItems(): void {
  void getDefaultStore().get(dealItemsRefetchState)?.fn();
}

/**
 * Retire les toasts "en cours" des imports lancés depuis cet onglet dont le statut
 * est devenu terminal, abouti comme échoué.
 *
 * Volontairement indépendant du suivi des transitions : un toast en attente et un
 * statut terminal suffisent à conclure. Le subordonner à un état précédent connu
 * ferait avaler la fin d'import par le tout premier signal `DEAL` de la session,
 * laissant un toast infini à l'écran — c'est aussi ce qui rattrape un import
 * terminé pendant une coupure réseau, au `SYNC` de reconnexion.
 *
 * Un échec ne fait que **nettoyer** le toast ici. Signaler l'erreur à l'utilisateur
 * reste du ressort du diff de transitions : un import déjà en échec au moment où on
 * arrive sur le deal ne doit pas produire de toast d'erreur à chaque chargement de
 * page, alors qu'un spinner resté affiché doit disparaître dans tous les cas.
 *
 * Un import abouti passe par une relecture dédiée : `GetDealById` ne sélectionne
 * pas `importSummary` (il n'existe que dans `GetDatasource`), et s'en passer
 * ouvrirait le rapport d'import avec des listes d'identifiants vides. La requête ne
 * part que pour un import réellement attendu et devenu terminal, soit au plus une
 * fois par import lancé depuis cet onglet.
 */
function resolveAwaitedImports(
  client: ApolloClient,
  deal: DealFromQuery,
  t: TFunction
): void {
  const { processed, failed } = reconcilePendingDatasources({
    pendingIds: pendingDatasourceToastIds(),
    dataSources: deal.dataSources,
  });

  for (const dataSource of failed) {
    clearDealItemsInProgressToast({
      datasourceId: dataSource.id,
      kind: 'datasourceProcessed',
    });
  }

  for (const dataSource of processed) {
    void resolveAwaitedProcessedDatasource(client, deal.id, dataSource.id, t);
  }
}

/**
 * Réagit à un signal `DEAL` du canal SSE, à partir du deal fraîchement relu.
 *
 * Dérive les transitions de l'état successif du deal (le signal ne porte aucun
 * payload) :
 * changement de version active, passage d'un datasource en erreur, et fin d'import
 * (rapport d'import + rechargement des items).
 *
 * Le réveil du fallback catégorie Shopify est déclenché ici plutôt que sur le seul
 * signal `ITEMS` : le writer ne conserve sous backpressure que la dernière action
 * `ITEMS`, donc un `datasourceProcessed` écrasé par un `itemsUpdated` ne
 * réveillerait jamais le fallback s'il n'était armé que par l'action. Le dériver du
 * passage d'un datasource à `processed` le rend insensible à ce coalescing. Le
 * fallback se désarme lui-même à la première prise en compte, donc un double
 * déclenchement est sans effet.
 *
 * Le premier appel pour un deal ne fait qu'enregistrer l'état : sans point de
 * comparaison il n'y a pas de transition, et signaler l'état initial ferait
 * apparaître des toasts au chargement de la page.
 *
 * Idempotent : l'état observé est mémorisé à chaque appel, donc rejouer le même
 * deal — ce qui arrive à chaque `SYNC` de reconnexion — ne redéclenche rien.
 */
export function resolveDealSignal({
  client,
  deal,
  t,
  toast,
}: {
  client: ApolloClient;
  deal: DealFromQuery;
  t: TFunction;
  toast: ShowDataSourceErrorToastParams['toast'];
}): void {
  const previous = trackingFor(deal);
  rememberDealState(deal);

  resolveAwaitedImports(client, deal, t);

  if (!previous) return;

  const hasVersionChanged =
    previous.activeVersionId !== (deal.activeVersionId ?? null);
  let hasNewlyProcessedDatasource = false;

  for (const dataSource of deal.dataSources) {
    const currentStatus = datasourceStatusOf(dataSource);
    const previousStatus = previous.statusByDatasourceId.get(dataSource.id);
    const isKnownDatasource = previous.statusByDatasourceId.has(dataSource.id);

    if (
      currentStatus === DataSourceStatus.Error &&
      isKnownDatasource &&
      previousStatus !== DataSourceStatus.Error
    ) {
      clearDealItemsInProgressToast({
        datasourceId: dataSource.id,
        kind: 'datasourceProcessed',
      });
      showDataSourceErrorToast({
        errors: 'errors' in dataSource ? [...(dataSource.errors ?? [])] : [],
        t,
        toast,
        titleKey: 'general.error_import',
        defaultMessageKey: 'general.error_import',
      });
    }

    if (
      currentStatus === DataSourceStatus.Processed &&
      previousStatus !== DataSourceStatus.Processed
    ) {
      hasNewlyProcessedDatasource = true;
    }
  }

  /**
   * Un seul rechargement par signal, quel que soit le nombre de raisons de le
   * faire. Une fin d'import change typiquement la version active **et** fait
   * passer le datasource à `processed` dans le même signal ; deux appels
   * n'aboutiraient pas à deux requêtes concurrentes — `ensureDealItemsStream` met
   * le second en file — mais à un stream d'items complet rejoué pour rien juste
   * après le premier.
   */
  if (hasVersionChanged || hasNewlyProcessedDatasource) {
    refetchDealItems();
  }

  /**
   * Indépendant du rechargement : arme le fallback catégorie Shopify, qui ne
   * dépend pas d'un stream d'items.
   */
  if (hasNewlyProcessedDatasource) {
    getDefaultStore().set(datasourceProcessedSignalAtom, (n) => n + 1);
  }
}

export function __resetDealSignalTrackingForTests(): void {
  tracking = null;
}
